'use client';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import styled from "styled-components";
import {Col, Container, Row} from "react-bootstrap";
import Select, {components} from "react-select";
import {black, hover} from "@/styles/globalStyleVars";
import ProjectBox from "@/components/project/ProjectBox";
import {usePathname, useRouter, useSearchParams} from "next/navigation";
import {getProjectSort} from "@/api";
import {ScrollTrigger} from "gsap/ScrollTrigger";
import debounce from 'lodash.debounce';
const MyComponent = ({data, optionList, filter}) => {
    const [isClient, setIsClient] = useState(false);
    const [isFilter, setIsFilter] = useState(filter);
    const pathname = usePathname(); // Get the current pathname as a string
    const router = useRouter();

// Define constants for the initial page and items per page
//     const p_location = isFilter?.project_location?.map(location => ({value: location?.value, label: location?.label}));
//     const project_category = isFilter?.project_collection?.map(location => ({value: location?.value, label: location?.label}));
//     const project_status = isFilter?.project_status?.map(location => ({value: location?.value, label: location?.label}));
//     const project_type = isFilter?.project_type?.map(location => ({value: location?.value, label: location?.label}));
//     const project_area = isFilter?.project_feature?.map(location => ({value: location?.value, label: location?.label}));


    const p_location = useMemo(() =>
        isFilter?.project_location?.map(location => ({
            value: location?.value,
            label: location?.label
        })), [isFilter?.project_location]);

    const project_category = useMemo(() =>
        isFilter?.project_collection?.map(location => ({
            value: location?.value,
            label: location?.label
        })), [isFilter?.project_collection]);

    const project_status = useMemo(() =>
        isFilter?.project_status?.map(location => ({
            value: location?.value,
            label: location?.label
        })), [isFilter?.project_status]);

    const project_type = useMemo(() =>
        isFilter?.project_type?.map(location => ({
            value: location?.value,
            label: location?.label
        })), [isFilter?.project_type]);

    const project_area = useMemo(() =>
        isFilter?.project_feature?.map(location => ({
            value: location?.value,
            label: location?.label
        })), [isFilter?.project_feature]);


    const [selectedData, setSelectedData] = useState(data?.list);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedType, setSelectedType] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedArea, setSelectedArea] = useState('');
    const [selectedLocation, setSelectedLocation] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const selectTypeInputRef = useRef();
    const selectStatusInputRef = useRef();
    const selectAreaInputRef = useRef();
    const selectLocationInputRef = useRef();


    const hlocation = usePathname();

    //To get the params
    const paramStatus = new URLSearchParams(hlocation.search).get("status");


    const customStyles = {
        dropdownIndicator: (base, state) => ({
            ...base,
            transition: "all .2s ease",
            transform: state.selectProps.menuIsOpen && "rotate(180deg)",
        }),
        control: (base, state) => ({
            ...base,
            backgroundColor: 'transparent',
            borderColor: "#B8B8B8",
            borderRadius: 0,
            height: 60,
            boxShadow: state.isFocused ? null : null,
            "&:hover": {
                borderColor: "#222222",
                // cursor: 'pointer'
            },
        }),
        option: (styles, state) => ({
            ...styles,
            backgroundColor: state.isSelected ? hover : '#FFF',
            "&:hover": {
                backgroundColor: "transparent",
                color: hover,
                // cursor: 'pointer'
            },
            zIndex : '99999',
            position: 'relative',
        }),
        indicatorContainer: (base, state) => ({
            ...base,
            transform: state.selectProps.menuIsOpen && "rotate(180deg)"
        }),
        menu: (base) => ({
            ...base,
            zIndex: 99999, // Increase the z-index value as needed
            fontSize:'12px',
            lineHeight:'16px'
        }),

    };


    // Set up client-side rendering detection
    useEffect(() => {
        setIsClient(true);
    }, []);


    // Unified state change handler with debounce-like delay to reduce router pushes
    const updateData = async (status, type, location, category, area) => {
        setLoading(true);
        try {
            const getHomeData = await getProjectSort(status, type, location, category, area);
            setSelectedData(getHomeData?.[0]?.list || []);
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const debouncedUpdateData = useRef(
        debounce((status, type, location, category, area) => {
            updateData(status, type, location, category, area);
        }, 300)
    ).current;

    // Handlers for each filter option
    const handleCategory = (e) => {
        setSelectedCategory(e?.value || '');
        selectTypeInputRef?.current?.clearValue();
        selectStatusInputRef?.current?.clearValue();
        selectAreaInputRef?.current?.clearValue();
        selectLocationInputRef?.current?.clearValue();
        debouncedUpdateData(selectedStatus, selectedType, selectedLocation, e?.value, selectedArea);


        router.replace(`/project?category=${e?.value || ''}`);
    };

    const handleStatus = (e) => {
        const newStatus = e?.value || '';
        setSelectedStatus(newStatus);
        selectTypeInputRef?.current?.clearValue();
        selectAreaInputRef?.current?.clearValue();
        selectLocationInputRef?.current?.clearValue();
        debouncedUpdateData(newStatus, selectedType, selectedLocation, selectedCategory, selectedArea);

        router.replace(`/project?category=${selectedCategory}&status=${newStatus}`);
    };

    const handleType = (e) => {
        const newType = e?.value || '';
        setSelectedType(newType);
        selectAreaInputRef?.current?.clearValue();
        selectLocationInputRef?.current?.clearValue();
        debouncedUpdateData(selectedStatus, newType, selectedLocation, selectedCategory, selectedArea);
        router.replace(`/project?category=${selectedCategory}&status=${selectedStatus}&type=${newType}`);
    };

    const handleArea = (e) => {
        const newArea = e?.value || '';
        setSelectedArea(newArea);
        setSelectedLocation('');
        selectLocationInputRef?.current?.clearValue();
        debouncedUpdateData(selectedStatus, selectedType, selectedLocation, selectedCategory, newArea);
        router.replace(`/project?category=${selectedCategory}&status=${selectedStatus}&type=${selectedType}&area=${newArea}`);
    };

    const handleLocation = (e) => {
        const newLocation = e?.value || '';
        setSelectedLocation(newLocation);
        debouncedUpdateData(selectedStatus, selectedType, newLocation, selectedCategory, selectedArea);
        router.replace(`/project?category=${selectedCategory}&status=${selectedStatus}&type=${selectedType}&area=${selectedArea}&location=${newLocation}`);
    };


    const searchParams = useSearchParams();


    const keywordCategory = searchParams.get("category");
    const keywordStatus = searchParams.get("status");
    const keywordType = searchParams.get("type");
    const keywordArea = searchParams.get("area");
    const keywordLocation = searchParams.get("location");




    useEffect(() => {
        const fetchData = async () => {

            if (keywordCategory || keywordStatus || keywordType || keywordArea || keywordLocation) {
                try {
                    const getHomeData = await getProjectSort(keywordStatus, keywordType, keywordLocation, keywordCategory, keywordArea);
                    setSelectedData(getHomeData?.[0]?.list || []);

                } catch (e) {
                    console.error(e); // Log any errors
                }
            }
        };

        fetchData();
    }, [searchParams]); // Only run when searchParams change


    useEffect(() => {
        selectedCategory?.current?.clearValue();
        selectTypeInputRef?.current?.clearValue();
        selectStatusInputRef?.current?.clearValue();
        selectAreaInputRef?.current?.clearValue();
        selectLocationInputRef?.current?.clearValue();
    }, [pathname]);


    const CaretDownIcon = () => {
        return <svg xmlns="http://www.w3.org/2000/svg" width="11.414" height="6.414" viewBox="0 0 11.414 6.414">
            <g id="Group_4823" data-name="Group 4823" transform="translate(10.707 0.707) rotate(90)">
                <line id="Line_9" data-name="Line 9" x2="5" y2="5" fill="none" stroke="#56575a" stroke-linecap="round" stroke-width="1"/>
                <line id="Line_10" data-name="Line 10" x1="5" y2="5" transform="translate(0 5)" fill="none" stroke="#56575a" stroke-linecap="round" stroke-width="1"/>
            </g>
        </svg>;
    };
    const DropdownIndicator = props => {
        return (
            <components.DropdownIndicator {...props}>
                <CaretDownIcon/>
            </components.DropdownIndicator>
        );
    };

    const CustomPlaceholder = ({ img,placeholder, ...props }) => {
        return (
            <div className={'custom-placeholder'}>
                <img src={img} />
                <p>{placeholder}</p>
            </div>
        );
    };


    // Fetches more data when the Load More button is clicked
    const loadMore = async () => {
        setLoading(true);
        const newPage = page + 1;
        try {
            // Fetch new data with the updated page number
            const getHomeData = await getProjectSort(
                selectedStatus,
                selectedType,
                selectedLocation,
                selectedCategory,
                selectedArea,
                newPage
            );

            // Concatenate new data with the existing data
            setSelectedData(prevData => [...prevData, ...(getHomeData?.[0]?.list || [])]);
            setPage(newPage);
        } catch (error) {
            console.error("Error loading more data:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        ScrollTrigger.refresh();
        // Scroll to top when page changes
        window.scrollTo(0, 1);
    }, [pathname]);



    return (
        <StyledComponent className={'pt-160 pb-120'}>
            <Container>
                <Row>
                    <Col >
                        <Select
                            onChange={handleCategory}
                            isSearchable={false}
                            styles={customStyles}
                            components={{ DropdownIndicator, Placeholder: (props) => <CustomPlaceholder img={'/images/dynamic/project/union.svg'} placeholder={'Category'} {...props} /> }}
                            options={project_category}
                            placeholder={selectedCategory ? selectedCategory : paramStatus ? paramStatus : CustomPlaceholder}
                            classNamePrefix={'react-select'}

                        />
                    </Col>
                    <Col>
                        <Select
                            onChange={handleStatus}
                            styles={customStyles}
                            options={project_status}
                            ref={selectStatusInputRef}
                            classNamePrefix={'react-select'}
                            components={{ DropdownIndicator, Placeholder: (props) => <CustomPlaceholder img={'/images/dynamic/project/a2.svg'} placeholder={'Status'} {...props} /> }}
                            isSearchable={false}
                            placeholder={'Status'}
                        />
                    </Col>
                    <Col>
                        <Select
                            onChange={handleType}
                            styles={customStyles}
                            options={project_type}
                            ref={selectTypeInputRef}
                            components={{ DropdownIndicator, Placeholder: (props) => <CustomPlaceholder img={'/images/dynamic/project/a3.svg'} placeholder={'Type'} {...props} /> }}
                            classNamePrefix={'react-select'}
                            isSearchable={false}
                            placeholder={'Type'}
                        />
                    </Col>
                    <Col>
                        <Select
                            styles={customStyles}
                            options={project_area}
                            ref={selectAreaInputRef}
                            onChange={handleArea}
                            classNamePrefix={'react-select'}
                            components={{ DropdownIndicator, Placeholder: (props) => <CustomPlaceholder img={'/images/dynamic/project/a4.svg'} placeholder={'Area(Sft)'} {...props} /> }}
                            isSearchable={false}
                            placeholder={'Area(Sft)'}

                        />
                    </Col>
                    <Col>
                        <Select
                            styles={customStyles}
                            options={p_location}
                            ref={selectLocationInputRef}
                            onChange={handleLocation}
                            classNamePrefix={'react-select'}
                            components={{ DropdownIndicator, Placeholder: (props) => <CustomPlaceholder img={'/images/dynamic/project/a5.svg'} placeholder={'Location'} {...props} /> }}
                            isSearchable={false}
                            placeholder={'Location'}
                        />
                    </Col>
                </Row>
            </Container>
            <div className={'border'}/>
            <Container>
                <Row>

                    {
                        selectedData && selectedData.length > 0 ?
                        selectedData?.map((e, index) => {

                            return(
                                <Col key={index} md={4}>
                                    <ProjectBox isSold={e?.project_list_sold} title={e?.post_title} link={`/project/${e?.post_name}`} type={e?.project_type} location={e?.project_location} img={e?.project_thumb} bedroom={e?.project_bedrooms} washroom={e?.project_list_bathrooms} rent={e?.project_category} area={e?.project_feature} />
                                </Col>
                            )
                        }) :
                            <Col>
                                <h2>No Data Found</h2>
                            </Col>
                    }

                </Row>
                {/*<Row >*/}
                {/*    <Col md={12} onClick={loadMore} className={'justify-content-center align-items-center'}>*/}
                {/*        <Button  text={'Load More'} src={"javascript:void(0)"} border={`1px`} borderColor={`${text}`} color={`${text}`} hoverBackground={`${hover}`}/>*/}
                {/*    </Col>*/}
                {/*</Row>*/}
            </Container>
        </StyledComponent>
    );
};
const StyledComponent = styled.section`
  
  background: #fff;
  padding-top: 160px;

  .css-hlgwow{
    display: flex !important;
  }

    .react-select__control {
        cursor: pointer;
    }

    .custom-placeholder {
    display: flex;
    align-items: center;

    img {
      margin-right: 10px;
    }

    p {
      font-size: 14px !important;
      font-style: normal;
      font-weight: 400;
      line-height: normal;
    }
  }

  .border {
    border-bottom: 1px solid rgba(19, 23, 25, 0.25);
    margin-bottom: 60px;
  }

  .react-select__menu {
    padding: 0;
    border-radius: 0;

    ul {
      margin: 0;
      padding: 0;
    }
  }

  .react-select__control {

    border: 1px solid transparent;
    height: 45px;
    background: #F6F6F8;
    border-radius: 5px;
    padding-left: 15px;
    padding-right: 15px;

    &:hover {
      border: 1px solid transparent;
    }

    &:focus {
      border: 1px solid transparent;
    }
  }


  .react-select__indicator-separator {
    display: none;
  }

  .react-select__single-value {
    color: #56575A !important;
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
  }

  .css-b62m3t-container {
    margin-bottom: 40px;
  }

  .css-1fdsijx-ValueContainer {
    display: flex !important;
    padding: 0;
  }

  .css-1jqq78o-placeholder {
    color: ${black} !important;
    font-size: 14px;
    line-height: 16px;
    font-weight: 400;
    text-transform: capitalize;
  }

  @media (max-width: 767px) {
    padding-top: 120px;
    .col{
      flex: 0 0 100%;
      max-width: 100%;
    }
    .css-b62m3t-container{
      margin-bottom: 10px !important;
    }
  }
`
export default MyComponent;